import React from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
import { Box, List, ListItemText, Typography } from '@mui/material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledNavItem, StyledNavItemIcon } from './styles';
import { tokenService } from 'src/services';

NavSection.propTypes = {
  data: PropTypes.array,
};

const generateCombinedPermissions = (
  accountCategory,
  frontAuthArray,
  userType
) => {
  const arr = Array.isArray(frontAuthArray) ? frontAuthArray : [];
  return arr.map((frontAuth) => `${accountCategory}:${frontAuth}:${userType}`);
};

const groupByCategory = (data) => {
  return data.reduce((groups, item) => {
    const category = item.category || '';
    groups[category] = groups[category] || [];
    groups[category].push(item);
    return groups;
  }, {});
};

export default function NavSection({ data = [], ...other }) {
  const user = tokenService.getUser();
  const accountCategory = user?.accountCategory || '';
  const userType = user?.type;
  const frontAuthArray = Array.isArray(user?.frontAuth) ? user.frontAuth : [];

  const combinedPermissions = generateCombinedPermissions(
    accountCategory,
    frontAuthArray,
    userType
  );

  const isItemAllowed = (item) => {
    if (
      Array.isArray(item.hiddenForFrontAuth) &&
      item.hiddenForFrontAuth.some((auth) => frontAuthArray.includes(auth))
    ) {
      return false;
    }

    if (item.permissionCombination && item.permissionCombination.length > 0) {
      const ok = item.permissionCombination.every((perm) =>
        frontAuthArray.includes(perm)
      );
      if (!ok) return false;
    }

    const required = Array.isArray(item.permissions) ? item.permissions : [];
    if (required.length === 0) return true;

    return required.some((permission) =>
      combinedPermissions.includes(permission)
    );
  };

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {Object.entries(groupByCategory(data)).map(([category, items]) => {
          const filteredItems = items.filter(
            (item) =>
              !item.hidden &&
              item.hiddenForAccountCategory !== accountCategory &&
              isItemAllowed(item)
          );

          if (filteredItems.length === 0) return null;

          return (
            <React.Fragment key={category}>
              {category && category !== 'Emanet İşlemleri' && (
                <Typography
                  sx={{
                    color: '#98ABEA',
                    fontSize: 12,
                    marginLeft: '20px',
                    fontWeight: '600',
                    marginBlock: '5px',
                  }}
                >
                  {category}
                </Typography>
              )}

              {filteredItems.map((item, index) => {
                // Accordion grubunu tek sefer basmak için: ilk elemanda accordion varsa
                if (item.accordion && index === 0) {
                  return (
                    <Accordion
                      key={category}
                      sx={{ bgcolor: 'transparent', boxShadow: 'none' }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon sx={{ color: '#E7EAFA' }} />
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          color: '#E7EAFA',
                          fontSize: 14,
                          paddingLeft: '0px',
                          minHeight: '48px',
                          '&.Mui-expanded': {
                            color: '#E7EAFA',
                            height: '48px',
                            minHeight: '40px',
                            alignItems: 'center',
                            display: 'flex',
                          },
                        }}
                      >
                        <StyledNavItemIcon>{item.icon}</StyledNavItemIcon>
                        <ListItemText
                          disableTypography
                          primary={item.category}
                          sx={{ textAlign: 'left', marginLeft: '0px' }}
                        />
                      </AccordionSummary>

                      <AccordionDetails>
                        {/* KRİTİK: Yetkili olanları bas */}
                        {filteredItems.map((subItem, subIndex) => (
                          <NavItem key={subIndex} item={subItem} />
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                }

                // Accordion dışı normal item
                if (!item.accordion) {
                  return <NavItem key={`${category}-${index}`} item={item} />;
                }

                // Accordion item'ların geri kalanlarını iki kere basmamak için null
                return null;
              })}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
};

function NavItem({ item }) {
  const { title, path, icon, info } = item;

  const user = tokenService.getUser();

  const currentBranchID = localStorage.getItem('branchID');
  const currentBranch =
    user?.authLocations?.branch?.find((b) => b.branchID === currentBranchID) ??
    user?.authLocations?.branch?.[0];

  const computedTitle =
    user?.accountCategory === 'store' && title === 'Hands Free'
      ? currentBranch?.mallID === '98569271082166328155'
        ? 'Bags-Free'
        : 'Hands Free'
      : title;

  return (
    <StyledNavItem
      component={RouterLink}
      to={path}
      sx={{
        color: '#E7EAFA',
        '&.active': {
          color: '#E7EAFA',
          bgcolor: '#0666F6',
          fontWeight: 'fontWeightBold',
        },
      }}
    >
      <StyledNavItemIcon>{icon}</StyledNavItemIcon>
      <ListItemText disableTypography primary={computedTitle} />
      {info && info}
    </StyledNavItem>
  );
}
